import { QuizQuestion, LessonCategory } from '@/types';

export const STATIC_QUIZ_DATA: Record<string, QuizQuestion[]> = {
  beginner: [
    {
      question: "What does AI stand for?",
      options: ["Artificial Intelligence", "Automated Information", "Advanced Integration", "Algorithmic Interface"],
      correct: 0,
      explanation: "AI stands for Artificial Intelligence. It refers to computer systems designed to perform tasks that usually require human intelligence, just like an autopilot system assists navigation but requires a course to follow."
    },
    {
      question: "Which of the following is an example of AI in everyday life?",
      options: ["A traditional calculator", "Voice assistants like Siri or Alexa", "A digital clock", "A microwave timer"],
      correct: 1,
      explanation: "Voice assistants use AI to understand speech, similar to how a bridge officer interprets radio communications, but using machine learning models instead of human cognition."
    },
    {
      question: "What is Machine Learning?",
      options: ["Teaching machines to move", "Programming robots", "A subset of AI where systems learn from data", "Computer hardware manufacturing"],
      correct: 2,
      explanation: "Machine Learning is like a cadet learning from experience. Instead of being explicitly programmed for every scenario, the system learns patterns from data (like past voyages) to improve over time."
    },
    {
      question: "In the context of AI, what is a 'Dataset'?",
      options: ["A computer chip", "A type of robot", "A programming language", "A collection of data used for training"],
      correct: 3,
      explanation: "A Dataset is like a ship's manifest or logbook. It contains all the information (examples) the AI studies to learn how to make predictions."
    },
    {
      question: "Why is 'Data Quality' important for AI?",
      options: ["Bad data leads to bad predictions", "It makes the computer look nice", "It is not important", "It saves electricity"],
      correct: 0,
      explanation: "Just as incorrect charts can lead a ship aground, 'Garbage In, Garbage Out' applies to AI. If the training data is inaccurate, the AI's decisions will be flawed."
    }
  ],
  intermediate: [
    {
      question: "What is the difference between supervised and unsupervised learning?",
      options: [
        "Supervised is faster than unsupervised",
        "Supervised uses labeled data, unsupervised finds patterns in unlabeled data",
        "Unsupervised requires human intervention",
        "They are exactly the same"
      ],
      correct: 1,
      explanation: "Supervised learning is like training a crew member with a manual (labels). Unsupervised learning is like dropping them in a new port and asking them to find their way around by observing patterns."
    },
    {
      question: "What is 'Overfitting' in a machine learning model?",
      options: [
        "When the model is too big",
        "When the model fits perfectly on new data",
        "When the model learns noise instead of the signal",
        "When the model runs too fast"
      ],
      correct: 2,
      explanation: "Overfitting is like a captain who only knows how to sail one specific route in perfect weather. They memorize the specific trip but fail when conditions change (new data)."
    },
    {
      question: "What is the difference between Classification and Regression?",
      options: [
        "Classification is for text; Regression is for images",
        "Regression is older than Classification",
        "They are the same thing",
        "Classification predicts categories; Regression predicts numbers"
      ],
      correct: 3,
      explanation: "Classification is like deciding if a ship is 'Late' or 'On Time' (categories). Regression is like estimating the exact 'Fuel Consumption' (a numerical value)."
    },
    {
      question: "What is Natural Language Processing (NLP)?",
      options: [
        "AI that understands and generates human language",
        "Processing shipping labels",
        "A programming language for logic",
        "Voice recording software"
      ],
      correct: 0,
      explanation: "NLP enables computers to understand text and speech, useful for analyzing charter parties, emails, and voyage reports automatically."
    },
    {
      question: "What is a Neural Network?",
      options: [
        "A network of computers on a ship",
        "A secure internet connection",
        "A computing system inspired by the biological brain",
        "A robotic arm controller"
      ],
      correct: 2,
      explanation: "Neural Networks mimic the human brain's interconnected neurons. They are powerful for complex tasks like weather routing, where many variables interact."
    }
  ],
  advanced: [
    {
      question: "What is the primary challenge addressed by attention mechanisms in transformers?",
      options: [
        "Computational efficiency",
        "Data preprocessing",
        "Long-range dependencies and context understanding",
        "Model compression"
      ],
      correct: 2,
      explanation: "Attention mechanisms help the model focus on relevant parts of the input sequence, similar to how a captain focuses on specific critical indicators on a dashboard while ignoring noise."
    },
    {
      question: "What is 'Hallucination' in Large Language Models?",
      options: [
        "When the AI displays images",
        "When the AI generates confident but factually incorrect information",
        "When the AI crashes",
        "When the AI becomes sentient"
      ],
      correct: 1,
      explanation: "Hallucination is when an AI fabricates facts, like a supercargo claiming a port exists where there is only open ocean. It sounds convincing but is wrong."
    },
    {
      question: "What is Reinforcement Learning (RL)?",
      options: [
        "Learning through trial and error based on rewards/punishments",
        "Learning from labeled datasets",
        "Hard-coding rules",
        "Scanning documents"
      ],
      correct: 0,
      explanation: "RL is like learning to dock a ship in a simulator. The system gets points (reward) for a smooth docking and loses points (punishment) for hitting the pier, eventually learning the optimal strategy."
    },
    {
      question: "What is 'Fine-tuning'?",
      options: [
        "Adjusting the screen brightness",
        "Restarting the server",
        "Writing a new algorithm from scratch",
        "Training a pre-trained model on a specific dataset"
      ],
      correct: 3,
      explanation: "Fine-tuning is taking a general-purpose model (like a general maritime cadet) and giving them specialized training on specific company procedures (your specific dataset)."
    },
    {
      question: "What is the 'Vanishing Gradient' problem?",
      options: [
        "When the screen fades out",
        "When data is lost during transmission",
        "When weights stop updating during training of deep networks",
        "When the learning rate is too high"
      ],
      correct: 2,
      explanation: "In deep networks, training signals can become incredibly weak as they pass back through layers, effectively stopping the learning process, much like a radio signal fading out over long distances."
    }
  ],
  expert: [
    {
      question: "In a transformer-based freight rate prediction model, what is the most likely cause of poor generalization to new trade routes?",
      options: [
        "Insufficient model parameters",
        "Distribution shift between training routes and unseen routes",
        "Using too many attention heads",
        "The learning rate is too low"
      ],
      correct: 1,
      explanation: "Distribution shift occurs when the statistical properties of new trade routes differ significantly from training data, like a captain trained only on Baltic routes struggling in the South China Sea."
    },
    {
      question: "When deploying an AI-based vessel ETA predictor, which architecture consideration is most critical for production reliability?",
      options: [
        "Using the largest model available",
        "Implementing graceful degradation with fallback heuristics",
        "Training on the most recent week of data only",
        "Maximizing GPU utilization"
      ],
      correct: 1,
      explanation: "Production AI systems must handle failures gracefully. If the ML model fails, rule-based fallbacks ensure operations continue, similar to how a ship has backup navigation systems."
    },
    {
      question: "What is the key advantage of using Graph Neural Networks (GNNs) for port congestion modeling?",
      options: [
        "They require less training data",
        "They naturally capture relational dependencies between interconnected ports",
        "They are faster than all other architectures",
        "They don't need GPUs"
      ],
      correct: 1,
      explanation: "GNNs model relationships between entities. Port networks are inherently graph-structured, where congestion at one hub ripples through connected ports like waves through a shipping lane network."
    },
    {
      question: "In multi-objective optimization for voyage planning, what is a Pareto front?",
      options: [
        "The fastest route between two ports",
        "The set of solutions where no objective can be improved without worsening another",
        "The front of the vessel that affects fuel consumption",
        "A preprocessing step for route data"
      ],
      correct: 1,
      explanation: "A Pareto front represents optimal trade-offs, like choosing between minimizing fuel cost and minimizing voyage time — improving one necessarily worsens the other."
    },
    {
      question: "Why is causal inference more valuable than correlation for maritime risk assessment AI?",
      options: [
        "Causal models are always more accurate",
        "Causal models can predict the effect of interventions, not just associations",
        "Correlation-based models cannot process maritime data",
        "Causal inference requires less data"
      ],
      correct: 1,
      explanation: "Knowing that bad weather correlates with delays is less useful than understanding that rerouting around a storm system causes a specific delay reduction — actionable vs. observational insight."
    }
  ],
  guru: [
    {
      question: "How should you handle concept drift in a real-time charter rate prediction system operating across multiple market cycles?",
      options: [
        "Retrain the model once per year on all historical data",
        "Implement online learning with adaptive windowing and drift detection algorithms",
        "Use only the most recent month of data",
        "Increase the model size to capture all patterns"
      ],
      correct: 1,
      explanation: "Market regimes shift (e.g., pre/post-pandemic). Adaptive windowing with drift detectors like ADWIN automatically adjusts the relevant training window, like a seasoned broker who knows when old market rules no longer apply."
    },
    {
      question: "What is the fundamental limitation of using standard backpropagation for optimizing discrete combinatorial decisions in stowage planning?",
      options: [
        "Backpropagation is too slow for real-time use",
        "Gradients are undefined for discrete variables, requiring relaxation or policy gradient methods",
        "Stowage planning has too few variables",
        "Neural networks cannot represent container data"
      ],
      correct: 1,
      explanation: "Discrete decisions (place container A in slot 7 or 12) have no smooth gradient. Techniques like Gumbel-Softmax relaxation or REINFORCE allow gradient-based optimization of inherently discrete maritime logistics."
    },
    {
      question: "In federated learning for a consortium of shipping companies, what is the primary attack vector that differential privacy aims to mitigate?",
      options: [
        "Model convergence failure",
        "Membership inference attacks that reveal if specific voyage data was in a company's training set",
        "Network latency between participants",
        "Unbalanced dataset sizes"
      ],
      correct: 1,
      explanation: "Without differential privacy, adversaries can determine whether a competitor's specific trade route data was used for training, leaking commercially sensitive information about fleet operations."
    },
    {
      question: "When applying Retrieval-Augmented Generation (RAG) to maritime regulatory compliance, what is the most critical failure mode to architect against?",
      options: [
        "Slow retrieval speed",
        "Faithful but outdated retrieval leading to non-compliance with amended regulations",
        "The LLM generating text that is too long",
        "Embedding model size"
      ],
      correct: 1,
      explanation: "Maritime regulations (SOLAS, MARPOL) are frequently amended. A RAG system that faithfully retrieves outdated regulation text is more dangerous than one that hallucinates, because the answer appears authoritative."
    },
    {
      question: "What makes sim-to-real transfer particularly challenging for autonomous vessel navigation AI compared to autonomous road vehicles?",
      options: [
        "Ships are larger than cars",
        "Maritime environments have non-stationary dynamics (currents, wind, wave interactions) that are fundamentally harder to simulate with fidelity",
        "There are fewer ships than cars",
        "GPS works differently at sea"
      ],
      correct: 1,
      explanation: "Ocean dynamics involve complex fluid-structure interactions that change continuously. Unlike road surfaces, the 'terrain' itself is in motion, creating a reality gap that makes simulator-trained policies brittle at sea."
    }
  ]
};

export const LESSON_CATEGORIES: LessonCategory[] = [
  { icon: '📊', title: 'Freight Trading', sub: 'Commercial Strategy' },
  { icon: '⚖️', title: 'Risk Management', sub: 'Hedging & FFAs' },
  { icon: '🚢', title: 'Voyage Optimization', sub: 'Efficiency' },
  { icon: '📄', title: 'Smart Documents', sub: 'Automation' },
  { icon: '📈', title: 'Market Prediction', sub: 'Forecasting' },
  { icon: '⚓', title: 'Port Operations', sub: 'Logistics' },
  { icon: '🤖', title: 'LLM Basics', sub: 'Understanding AI' },
  { icon: '💬', title: 'Prompting', sub: 'Talking to AI' }
];
